import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eyebrow, Card } from './UI';
import { Zap, Hexagon, Plus, Minus } from 'lucide-react';
export const Materials = () => {
  const materials = [{
    tag: 'SHELL',
    name: 'GFR COMPOSITE ALLOY SHELL',
    spec: 'GLASS-FIBER REINFORCED POLYMER',
    badge: 'CRUSH RESISTANT',
    body: 'Aerospace-category composite construction. The same material class used in protective aviation helmets. Flexes on impact and returns to form. 300+ lbs of crush resistance. Not ABS. Not PVC. Not plastic. Entirely different category.'
  }, {
    tag: 'SKELETON',
    name: 'STAINLESS STEEL INNER FRAME',
    spec: 'GRADE 304 SURGICAL ALLOY',
    badge: 'SURGICAL GRADE',
    body: 'Beneath the composite shell runs an internal Grade 304 stainless steel skeleton — the same alloy in surgical instruments. It distributes lateral impact force across the full frame. Zero flex failure. Zero structural compromise.'
  }, {
    tag: 'LENSES',
    name: 'POLYCARBONATE LENS SYSTEM',
    spec: 'ANSI Z87.1 — ANTI-FOG — UV400',
    badge: 'SHATTER RESISTANT',
    body: 'Lexan-grade polycarbonate — the same material class used in bullet-resistant glass. Anti-fog nano-coating on interior. UV400 protection standard across all 6 lens colors. Tool-free swap in under 30 seconds.'
  }, {
    tag: 'LINING',
    name: 'MILITARY NEOPRENE PADDING',
    spec: 'ANTI-BACTERIAL | MOISTURE-WICKING',
    badge: 'FIELD GRADE COMFORT',
    body: 'The face contact zone is lined with dense military-spec neoprene — the same grade used in tactical plate carrier padding. Anti-bacterial. Moisture-wicking. Thick enough to absorb impact without collapsing against your face during extended wear.'
  }, {
    tag: 'STRAP',
    name: 'BALLISTIC NYLON STRAP',
    spec: 'DUAL-POINT ADJUST — QUICK RELEASE',
    badge: 'MOLLE-SPEC NYLON',
    body: 'The retention system uses ballistic nylon webbing — the exact material in MOLLE tactical gear and plate carrier straps. 500+ hours of field use without elastic degradation. Anti-slip silicone lining. Fits 52cm–62cm head circumference.'
  }, {
    tag: 'FINISH',
    name: 'CERAMIC-INFUSED SURFACE COAT',
    spec: 'FADE PROOF | SCRATCH RESISTANT | UV STABLE',
    badge: 'PERMANENT BOND',
    body: 'The matte black finish is a ceramic-infused surface treatment bonded directly to the composite shell. Not paint. Not coating. Bonded. UV-stable. Scratch-resistant. No fading, no peeling — even after hundreds of field hours under direct sun.'
  }];
  const bars = [{
    label: 'IMPACT RESISTANCE',
    value: 96
  }, {
    label: 'LENS OPTICAL CLARITY',
    value: 98
  }, {
    label: 'MATERIAL DURABILITY',
    value: 94
  }, {
    label: 'FIELD COMFORT',
    value: 91
  }];
  const [openIndex, setOpenIndex] = useState<number>(0);
  return <section className="relative pt-[40px] pb-[40px] overflow-hidden bg-base-black" data-id="element-308">
      {/* Background Video restricted to the top half */}
      <div className="absolute top-0 left-0 right-0 h-[800px] z-0" data-id="element-309">
        <video autoPlay loop muted playsInline className="w-full h-full object-cover opacity-100" data-id="element-310">
          <source src="https://cdn.magicpatterns.com/uploads/fNp48KrVEknbEQusJkUVKW/hf_20260506_142518_1e16225a-b5f2-42aa-9e84-e3f4a3300fe1.mp4" type="video/mp4" data-id="element-311" />
        </video>
        {/* Dark overlay to ensure text readability and fade seamlessly into the black background below */}
        <div className="absolute inset-0 bg-gradient-to-b from-base-black/90 via-base-black/30 to-base-black" data-id="element-312"></div>
      </div>

      <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-313">
        {/* Intro */}
        <div className="text-center max-w-4xl mx-auto mb-16" data-id="element-314">
          <Eyebrow data-id="element-315">ENGINEERING REPORT</Eyebrow>
          <h2 className="text-5xl md:text-[80px] leading-[0.9] mb-6" data-id="element-316">
            THIS ISN'T PLASTIC. THIS IS{' '}
            <span className="text-acid-green" data-id="element-317">BLACKOUT UNIT™</span> SPEC.
          </h2>
          <p className="font-barlow text-[17px] text-text-body leading-[1.9] max-w-[700px] mx-auto" data-id="element-318">
            "Every mask in our category is made from the same injection-molded
            ABS plastic that's been used since the 90s. It shatters. It warps.
            It fogs. It fails. We went back to zero. We asked one question: What
            would you build if there were no budget for failure?"
          </p>
        </div>

        {/* WOW Callout Box */}
        <div className="bg-acid-green-dim border border-acid-green-border border-l-4 border-l-acid-green p-8 md:p-10 max-w-[900px] mx-auto mb-20 relative" data-id="element-319">
          <div className="flex items-start gap-4" data-id="element-320">
            <Zap className="text-acid-green shrink-0 mt-1" size={28} data-id="element-321" />
            <div data-id="element-322">
              <h3 className="font-bebas text-[22px] tracking-[3px] text-white mb-3" data-id="element-323">
                STRESS TEST RESULT — FIELD VERIFICATION
              </h3>
              <p className="font-barlow text-[17px] text-text-body leading-[1.8]" data-id="element-324">
                "We placed the BLACKOUT UNIT Ultimate Mask on the ground and had
                a loaded F-250 Super Duty — 6,500 lbs — roll its front tire
                directly over the shell. The mask flexed. Absorbed the
                compression. And returned to shape. We put it back on. It still
                fit. The lenses didn't crack. That's not a stunt. That's what
                happens when you build with Glass-Fiber Reinforced composite
                instead of injection plastic."
              </p>
            </div>
          </div>
        </div>

        {/* Material Spec Sheet — Interactive Dossier */}
        <div className="max-w-[1100px] mx-auto mb-20 border border-dark-3 bg-dark-1" data-id="element-325">
          {/* Header row */}
          <div className="hidden md:grid grid-cols-[60px_120px_1fr_180px_60px] items-center gap-4 px-6 py-3 border-b border-dark-3 bg-base-black" data-id="element-326">
            <div className="font-barlow text-[10px] text-text-dimmed tracking-[2px]" data-id="element-327">
              INDEX
            </div>
            <div className="font-barlow text-[10px] text-text-dimmed tracking-[2px]" data-id="element-328">
              COMPONENT
            </div>
            <div className="font-barlow text-[10px] text-text-dimmed tracking-[2px]" data-id="element-329">
              DESIGNATION
            </div>
            <div className="font-barlow text-[10px] text-text-dimmed tracking-[2px]" data-id="element-330">
              CLASSIFICATION
            </div>
            <div className="font-barlow text-[10px] text-text-dimmed tracking-[2px] text-right" data-id="element-331">
              EXPAND
            </div>
          </div>

          {materials.map((m, i) => {
          const isOpen = openIndex === i;
          return <div key={i} className={`border-b border-dark-3 last:border-b-0 ${isOpen ? 'bg-base-black' : ''}`} data-id="element-332">
                <button onClick={() => setOpenIndex(isOpen ? -1 : i)} className="w-full grid grid-cols-[40px_1fr_40px] md:grid-cols-[60px_120px_1fr_180px_60px] items-center gap-4 px-6 py-5 text-left hover:bg-base-black transition-colors group" data-id="element-333">
                  <div className={`font-bebas text-[22px] leading-none ${isOpen ? 'text-acid-green' : 'text-text-dimmed group-hover:text-acid-green'} transition-colors`} data-id="element-334">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="hidden md:block font-barlow text-[11px] text-acid-green tracking-[2px]" data-id="element-335">
                    {m.tag}
                  </div>
                  <div data-id="element-336">
                    <h4 className="font-bebas text-[18px] md:text-[20px] text-white leading-none" data-id="element-337">
                      {m.name}
                    </h4>
                    <div className="md:hidden font-barlow text-[10px] text-acid-green tracking-[1.5px] mt-1" data-id="element-338">
                      {m.tag} — {m.spec}
                    </div>
                    <div className="hidden md:block font-barlow text-[11px] text-text-dimmed tracking-[1px] mt-1" data-id="element-339">
                      {m.spec}
                    </div>
                  </div>
                  <div className="hidden md:block" data-id="element-340">
                    <span className="inline-block bg-acid-green text-black font-barlow font-bold text-[10px] tracking-[2px] px-3 py-1" data-id="element-341">
                      {m.badge}
                    </span>
                  </div>
                  <div className="flex justify-end" data-id="element-342">
                    <div className={`w-8 h-8 border flex items-center justify-center transition-colors ${isOpen ? 'border-acid-green text-acid-green' : 'border-dark-3 text-text-dimmed group-hover:border-acid-green group-hover:text-acid-green'}`} data-id="element-343">
                      {isOpen ? <Minus size={14} data-id="element-344" /> : <Plus size={14} data-id="element-345" />}
                    </div>
                  </div>
                </button>
                <AnimatePresence initial={false} data-id="element-346">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeOut'
              }} className="overflow-hidden" data-id="element-347">
                      <div className="px-6 pb-6 md:pl-[180px] md:pr-[60px] grid md:grid-cols-[1fr_auto] gap-6 items-start" data-id="element-348">
                        <p className="font-inter text-[14px] text-text-body leading-[1.8] border-l-2 border-acid-green pl-4" data-id="element-349">
                          {m.body}
                        </p>
                        <div className="md:hidden" data-id="element-350">
                          <span className="inline-block bg-acid-green text-black font-barlow font-bold text-[10px] tracking-[2px] px-3 py-1" data-id="element-351">
                            {m.badge}
                          </span>
                        </div>
                        <div className="hidden md:flex items-center gap-2 text-acid-green" data-id="element-352">
                          <Hexagon size={16} data-id="element-353" />
                          <span className="font-barlow text-[10px] tracking-[2px]" data-id="element-354">
                            VERIFIED
                          </span>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        {/* Performance Bars */}
        <div className="border border-acid-green/20 relative overflow-hidden p-8 md:p-12" data-id="element-355">
          {/* Background Image for this specific block */}
          <div className="absolute inset-0 z-0" data-id="element-356">
            <img src="https://cdn.magicpatterns.com/uploads/w6GEHJzDHQ5XRpNSJscbev/hf_20260506_142335_7f850f1d-a7b9-429c-843a-31d458d2a415.png" alt="Structural Performance" className="w-full h-full object-cover opacity-70" data-id="element-357" />
            <div className="absolute inset-0 bg-gradient-to-b from-base-black/70 via-base-black/40 to-base-black/70" data-id="element-358"></div>
          </div>

          <div className="relative z-10" data-id="element-359">
            <h3 className="font-bebas text-3xl text-center mb-10" data-id="element-360">
              STRUCTURAL PERFORMANCE RATINGS
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-361">
              {bars.map((bar, i) => <div key={i} data-id="element-362">
                  <div className="flex justify-between font-barlow text-sm tracking-wider mb-2" data-id="element-363">
                    <span className="text-white" data-id="element-364">{bar.label}</span>
                    <span className="text-acid-green" data-id="element-365">{bar.value}/100</span>
                  </div>
                  <div className="h-2 bg-dark-3 w-full overflow-hidden" data-id="element-366">
                    <motion.div initial={{
                  width: 0
                }} whileInView={{
                  width: `${bar.value}%`
                }} viewport={{
                  once: true,
                  margin: '-50px'
                }} transition={{
                  duration: 1.5,
                  ease: 'easeOut',
                  delay: i * 0.2
                }} className="h-full bg-gradient-to-r from-acid-green/50 to-acid-green" data-id="element-367" />
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
};